import { useParams } from 'react-router-dom';
import Breadcrumbs from 'components/common/Breadcrumbs';
import DealDetailsCard from 'components/modules/CRM/Deals/DealDetailsCard';
import DealAssociation, {
  AssociatedContact,
} from 'components/modules/CRM/Deals/DealAssociation';
import ContactDetailsSkeleton from 'components/Loaders/ContactDetailsSkeleton';
import { useCrmNavigation } from 'hooks/useCrmNavigation';
import {
  useDealDetailsByKey,
  useDealContacts,
  useDealCompanies,
} from 'service/crm';
import { getDaysLeft, timeAgo } from 'utils/date';
import { contactDisplayName } from 'utils/crmContact';
import { Stat } from 'components/common/StatCard';
import DealActivity from 'components/modules/CRM/Deals/DealActivity';
import { RecordAccessProvider } from 'context/crm/RecordAccessContext';
import { formatDealUsdAmount } from 'utils/helpers';

const DealDetails = () => {
  const { dealKey } = useParams<{ dealKey: string }>();
  const { buildDealsUrl, buildViewDealUrl } = useCrmNavigation();
  // The URL carries the human-readable dealKey; every API call needs the uuid.
  const { dealUuid, deal, isLoading, isError } = useDealDetailsByKey(
    dealKey ?? '',
  );
  const { data: associatedContacts, isLoading: isLoadingContacts } =
    useDealContacts(dealUuid ?? '');
  const { data: associatedCompanies, isLoading: isLoadingCompanies } =
    useDealCompanies(dealUuid ?? '');

  const associationTimestamps = [
    deal?.createdAt && {
      label: 'Created',
      value: new Date(deal.createdAt).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
    },
    deal?.lastActivityAt && {
      label: 'Last activity',
      value: timeAgo(deal.lastActivityAt),
    },
    deal?.stageChangedAt && {
      label: 'Stage changed',
      value: timeAgo(deal.stageChangedAt),
    },
  ].filter(Boolean) as { label: string; value: string }[];

  if (isLoading) return <ContactDetailsSkeleton />;
  if (isError || !deal) return <div className="p-6 text-white">Deal not found</div>;

  const daysToClose = deal.closeDate ? getDaysLeft(deal.closeDate) : null;

  const contacts: AssociatedContact[] = (associatedContacts ?? []).map((c) => ({
    uuid: c.uuid,
    // Carried through so the row can link to /{orgSlug}/crm/{contactKey}.
    contactKey: c.contactKey,
    name: contactDisplayName(c),
    email: c.email,
    profileImageUrl: c.profileImageUrl ?? null,
  }));

  // TODO: remove — debugging the deal value from the backend
  console.log("[Deal] values from API:", {
    api: `GET /crm/deals/${deal.uuid}`,
    amount: deal.amount,
    currency: deal.currency,
    usdAmount: deal.usdAmount,
    dealValue: deal.dealValue,
  });

  const overdueTasks = deal.overdueTasks ?? 0;
 
     const stats: Stat[] = [
         {
             label: "Deal value",
             // USD, like the Contact/Company KPI — converted by the backend
             // (`usdAmount`). Falls back to the deal's own currency when the
             // backend couldn't convert it, so we never show a wrong `$` amount.
             value: formatDealUsdAmount(deal),
             accent: "success",
         },
         {
             label: "Overdue tasks",
             value: overdueTasks,
             // Only flag it when there is something to act on.
             ...(overdueTasks > 0 ? { accent: "warning" as const } : {}),
         },
         { label: "Activities", value: deal.activitiesCount ?? 0 },
         { label: "Notes", value: deal.notesCount ?? 0 },
     ];

  return (
    /**
     * RBAC: resolves this deal's access once — CRM Admin/Manager get full access,
     * a CRM User gets edit access only via `deal.canEdit` (Owner, assigned Task,
     * or assigned Note on THIS deal — section 19). The details card, the shared
     * Activity tree and the association panel all read it via `useRecordAccess()`.
     *
     * Independent of the associated contacts' and companies' own access
     * (sections 20, 21).
     */
    <RecordAccessProvider record={deal}>
      <div className="min-h-0 flex-1 flex flex-col text-white">
        <Breadcrumbs
          items={[
            { label: 'Deals', path: buildDealsUrl() },
            { label: `${deal.dealKey}`, path: buildViewDealUrl(dealKey as string) },
          ]}
          activeLinkProps={{ className: 'text-neutral font-semibold' }}
          className="px-6 shrink-0"
        />

        {/* ---------- Three column layout ---------- */}
        <div className="flex-1 min-h-0 bg-primary border-t border-primary-subtle mt-3 px-2">
          <div className="grid grid-cols-1 md:grid-cols-12 items-start h-full min-h-0 lg:grid-rows-[minmax(0,1fr)]">
            <div className="md:col-span-12 lg:col-span-3 space-y-6 lg:h-full lg:overflow-hidden">
              <DealDetailsCard deal={deal} />
            </div>

            <div className="md:col-span-8 lg:col-span-6 space-y-6 lg:h-full lg:min-h-0 lg:overflow-y-auto">
              {/* <Activity contactUuid='CT003' /> */}
              <DealActivity dealUuid={deal.uuid} />
            </div>

            <div className="md:col-span-12 lg:col-span-3 space-y-6 lg:h-full lg:min-h-0 lg:overflow-y-auto">
              <DealAssociation
                dealUuid={deal.uuid}
                stats={stats}
                contacts={contacts}
                companies={associatedCompanies ?? []}
                isLoadingContacts={isLoadingContacts}
                isLoadingCompanies={isLoadingCompanies}
                timestamps={associationTimestamps}
              />
            </div>
          </div>
        </div>
      </div>
    </RecordAccessProvider>
  );
};

export default DealDetails;